<template>
  <div class="accounts-step" :class="{ 'flow-step--readonly': viewOnly }">
    <div class="accounts-step__inner">
      <article class="accounts-details-card">
        <h3 class="accounts-details-card__heading">Account details</h3>
        <div class="accounts-details-card__body">
          <div class="accounts-line accounts-line--total">
            <span class="accounts-line__label">Total</span>
            <span class="accounts-line__value">{{
              formatAccountAmount(grandTotal)
            }}</span>
          </div>

          <div class="accounts-details-card__group">
            <div class="accounts-line accounts-line--sub">
              <span class="accounts-line__label">Rajput bill</span>
              <span class="accounts-line__value">{{
                formatAccountAmount(rajputBillAmount)
              }}</span>
            </div>
            <div
              class="accounts-line accounts-line--sub accounts-line--sub-nested"
            >
              <span class="accounts-line__label">Internal goods</span>
              <span class="accounts-line__value">{{
                formatAccountAmount(
                  serviceRequest.vendorInvoice ? internalGoodsTotal : 0
                )
              }}</span>
            </div>
            <div class="accounts-line accounts-line--sub">
              <span class="accounts-line__label">{{ vendorCardTitle }}</span>
              <span class="accounts-line__value">{{
                formatAccountAmount(rajdootBillAmount)
              }}</span>
            </div>
          </div>

          <div class="accounts-details-card__group">
            <div class="accounts-line accounts-line--meta">
              <span class="accounts-line__label">Rajput bill number</span>
              <span class="accounts-line__value accounts-line__value--meta">{{
                rajputBillNumberDisplay
              }}</span>
            </div>
            <div class="accounts-line accounts-line--meta">
              <span class="accounts-line__label">Rajdoot bill number</span>
              <span class="accounts-line__value accounts-line__value--meta">{{
                rajdootBillNumberDisplay
              }}</span>
            </div>
          </div>

          <div class="accounts-details-card__group accounts-details-card__group--summary">
            <div class="accounts-line">
              <span class="accounts-line__label">Received amount</span>
              <span class="accounts-line__value">{{
                formatAccountAmount(paidAmount)
              }}</span>
            </div>
            <div class="accounts-line accounts-line--remaining">
              <span class="accounts-line__label">Remaining amount</span>
              <span class="accounts-line__value">{{
                formatAccountAmount(outstanding)
              }}</span>
            </div>
          </div>
        </div>
      </article>
    </div>

    <el-dialog
      v-if="!viewOnly"
      :visible.sync="showTransactionForm"
      title="Add Transaction"
    >
      <div class="row">
        <div class="col-md-6">
          <base-input
            v-model="serviceRequestTransaction.amount"
            label="Amount"
          />
        </div>
        <div class="col-md-6">
          <base-input label="Payment Mode">
            <el-select v-model="serviceRequestTransaction.paymentModeId">
              <el-option
                v-for="(item, index) in paymentModes"
                :key="index"
                :label="item.label"
                :value="item.paymentModeId"
              />
            </el-select>
          </base-input>
        </div>
        <div class="col-md-6">
          <base-input label="Is Advance Amount?">
            <el-radio
              v-model="serviceRequestTransaction.isAdvance"
              :label="true"
              >Yes</el-radio
            >
            <el-radio
              v-model="serviceRequestTransaction.isAdvance"
              :label="false"
              >No</el-radio
            >
          </base-input>
        </div>
        <div class="col-md-6">
          <base-input
            v-uppercase
            v-model="serviceRequestTransaction.paymentNote"
            label="Payment Note"
          />
        </div>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="showTransactionForm = false">Cancel</el-button>
        <el-button type="primary" @click="addTransaction()">Confirm</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import {
  Button,
  Dialog,
  Select,
  Option,
  Radio
} from "element-ui";
import { mapActions, mapState, mapGetters, mapMutations } from "vuex";
import branchDetailsMixin from "@/mixins/branchDetailsMixin";
import { vendorDisplayName } from "@/utils/serviceRequestFlowHelpers";
import serviceRequestFlowViewOnlyMixin from "@/mixins/serviceRequestFlowViewOnlyMixin";

export default {
  mixins: [branchDetailsMixin, serviceRequestFlowViewOnlyMixin],
  directives: {
    uppercase: {
      inserted: function(el, _, vnode) {
        el.addEventListener("input", async function(e) {
          const modified = e.target.value
            .split(" ")
            .map(i => i.charAt(0).toUpperCase() + i.slice(1))
            .join(" ");
          vnode.componentInstance.$emit("input", modified);
        });
      }
    }
  },
  components: {
    [Button.name]: Button,
    [Dialog.name]: Dialog,
    [Select.name]: Select,
    [Option.name]: Option,
    [Radio.name]: Radio
  },
  data() {
    return {
      showTransactionForm: false,
      chalanTotal: 0,
      outstanding: 0,
      paidAmount: 0,
      laborCostTotal: 0,
      externalGoodsTotal: 0,
      grandTotal: 0,
      internalGoodsTotal: 0
    };
  },
  computed: {
    ...mapState("serviceRequestTransaction", ["serviceRequestTransaction"]),
    ...mapState("serviceRequest", ["serviceRequest"]),
    ...mapGetters({
      paymentModes: "paymentMode/getPaymentModes"
    }),
    vendorName() {
      return vendorDisplayName(this.serviceRequest);
    },
    vendorCardTitle() {
      const vendor = this.serviceRequest && this.serviceRequest.vendor;
      if (!vendor || typeof vendor !== "object") return this.vendorName;
      const firm = (vendor.firmName || vendor.firm_name || "").trim();
      if (firm) return firm;
      return this.vendorName;
    },
    rajputBillAmount() {
      return this.serviceRequest.saleId ? this.chalanTotal : 0;
    },
    rajdootBillAmount() {
      if (!this.serviceRequest.vendorInvoice) return 0;
      return (this.laborCostTotal || 0) + (this.externalGoodsTotal || 0);
    },
    rajputBillNumberDisplay() {
      const sr = this.serviceRequest || {};
      const num =
        sr.saleInvoiceNumber ||
        sr.saleNumber ||
        sr.saleBillNumber ||
        (sr.saleId ? sr.saleId : "");
      return num ? String(num) : "—";
    },
    rajdootBillNumberDisplay() {
      const num = this.serviceRequest && this.serviceRequest.vendorInvoice;
      return num ? String(num) : "—";
    }
  },
  mounted() {
    this.loadBranchDetails();
    this.getPaymentModes();
    this.getServiceRequestTransactions(this.$route.params.id);
    this.fetchServiceRequestAccountDetails(this.$route.params.id);
    this.getStatusList();
  },
  methods: {
    ...mapActions({
      getPaymentModes: "paymentMode/getPaymentModes",
      saveServiceRequestTransaction:
        "serviceRequestTransaction/saveServiceRequestTransaction",
      getServiceRequestTransactions:
        "serviceRequestTransaction/getServiceRequestTransactions",
      getServiceRequestAccountDetails:
        "serviceRequest/getServiceRequestAccountDetails",
      getStatusList: "status/getStatusList"
    }),
    ...mapMutations({
      resetServiceRequestTransaction:
        "serviceRequestTransaction/resetServiceRequestTransaction"
    }),
    formatAccountAmount(amount) {
      const value = parseFloat(amount) || 0;
      return new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
        minimumFractionDigits: 1,
        maximumFractionDigits: 1
      }).format(value);
    },
    openTransactionDialog() {
      if (this.viewOnly) return;
      if (this.serviceRequest.status.code != "completed") {
        this.showTransactionForm = true;
      } else {
        this.$notify({
          message: "Sorry, you cannot add transaction with completed status!",
          type: "danger",
          title: "Failure"
        });
      }
    },
    addTransaction() {
      if (this.viewOnly) return;
      this.saveServiceRequestTransaction({
        type: "no-advance",
        serviceRequestId: this.$route.params.id
      }).then(() => {
        this.showTransactionForm = false;
        this.resetServiceRequestTransaction();
        this.getServiceRequestTransactions(this.$route.params.id);
        this.fetchServiceRequestAccountDetails(this.$route.params.id);
        this.$notify({
          message: "Transaction Added Successfully!",
          type: "success",
          title: "Success"
        });
      });
    },

    fetchServiceRequestAccountDetails(serviceRequestId) {
      this.getServiceRequestAccountDetails(serviceRequestId).then(async res => {
        this.chalanTotal = res.data.chalanTotal;
        this.paidAmount = res.data.paidAmount;
        this.laborCostTotal = res.data.serviceRequestLaborTotal;
        this.externalGoodsTotal = res.data.externalGoodsTotal;
        this.internalGoodsTotal = res.data.internalGoodsTotal;
        this.grandTotal = this.serviceRequest.vendorInvoice
          ? this.laborCostTotal + this.externalGoodsTotal
          : 0;
        this.grandTotal = this.serviceRequest.saleId
          ? this.grandTotal + this.chalanTotal
          : this.grandTotal + 0;
        this.outstanding = this.grandTotal - this.paidAmount;
      });
    }
  }
};
</script>

<style scoped>
.accounts-step {
  width: 100%;
  padding: 4px 0 12px;
  box-sizing: border-box;
}
.accounts-step__inner {
  width: 100%;
  max-width: none;
  margin: 0;
}
.accounts-details-card {
  background: #fff;
  border: 1px solid #93c5fd;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 0 0 1px #bfdbfe;
}
.accounts-details-card__heading {
  margin: 0;
  padding: 16px 20px 12px;
  font-size: 17px;
  font-weight: 700;
  color: #111827;
  letter-spacing: -0.01em;
  border-bottom: 1px solid #e5e7eb;
}
.accounts-details-card__body {
  padding: 4px 20px 18px;
}
.accounts-details-card__group {
  padding: 4px 0;
  border-bottom: 1px solid #f3f4f6;
}
.accounts-details-card__group--summary {
  border-bottom: none;
  padding-top: 8px;
}
.accounts-line {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  padding: 11px 0;
  font-size: 14px;
  line-height: 1.4;
}
.accounts-line__label {
  flex: 1;
  min-width: 0;
  text-align: left;
  color: #374151;
  font-weight: 500;
}
.accounts-line__value {
  flex-shrink: 0;
  text-align: right;
  font-weight: 600;
  color: #111827;
  font-variant-numeric: tabular-nums;
  max-width: 50%;
  word-break: break-word;
}
.accounts-line--total {
  padding: 14px 0 12px;
}
.accounts-line--total .accounts-line__label {
  font-size: 15px;
  font-weight: 700;
  color: #111827;
}
.accounts-line--total .accounts-line__value {
  font-size: 17px;
  font-weight: 700;
}
.accounts-line--sub {
  padding-left: 8px;
}
.accounts-line--sub-nested {
  padding-left: 20px;
}
.accounts-line--sub-nested .accounts-line__label,
.accounts-line--sub-nested .accounts-line__value {
  font-size: 13px;
  color: #4b5563;
  font-weight: 500;
}
.accounts-line--sub .accounts-line__label,
.accounts-line--sub .accounts-line__value {
  color: #2563eb;
}
.accounts-line--sub .accounts-line__value {
  font-weight: 600;
}
.accounts-line--meta .accounts-line__label {
  color: #6b7280;
}
.accounts-line__value--meta {
  color: #2563eb !important;
  font-weight: 600 !important;
}
.accounts-line--remaining .accounts-line__value {
  color: #dc2626;
  font-weight: 700;
}
</style>
